import React, { useCallback, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';
import MathContent from '../../../math-content';
import { Container, Icon, Value, Badge } from '../';

import RemoveIconPath from '../../assets/close-icon.svg?react';

export const InsertedContainer = styled(Container)`
    box-shadow: var(--shadow-1);
    background-color: var(--blue50);
    border: 1px solid var(--blue500);

    &:hover {
        border: 1px solid var(--blue700);
        box-shadow: var(--shadow-2);
        color: var(--gray800);
    }

    &:active {
        box-shadow: var(--shadow-4);
        border: 1px solid var(--blue400);
        background-color: var(--blue50);
    }

    &:disabled {
        background-color: var(--color-white);
        box-shadow: none;
        border: 1px solid var(--gray200);
        cursor: initial;
    }

    ${(props) =>
        props.selected &&
        css`
            border: 1px solid var(--blue500);
            box-shadow: var(--shadow-3);
            color: var(--blue500);
        `}

    ${(props) =>
        props.isDragging &&
        css`
            border: 1px dashed var(--blue400) !important;
            box-shadow: none !important;
        `}

        ${(props) =>
        props.isOver &&
        css`
            box-shadow: var(--outline);
        `}
`;

export const InsertedBadge = styled(Badge)`
    cursor: pointer;
    background-color: var(--blue500);
    color: var(--color-white);

    ${InsertedContainer}:hover & {
        background-color: var(--blue700);
    }

    ${InsertedContainer}:active & {
        background-color: var(--blue400);
    }

    ${(props) =>
        props.disabled &&
        css`
            background-color: var(--gray50);
            color: var(--gray800);

            ${InsertedContainer}:hover & {
                background-color: var(--gray50);
            }
        `}
`;

export const RemoveIcon = styled(RemoveIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-white']};

        &[disabled] {
            fill: ${theme.colors['surface-grey-700']};
        }
    `}
`;

const InsertedChip = ({ id, value, handleClick, scrolling, disabled = false, selected = false, onClose, isDragging, isOver }) => {
    const ref = useRef(null);
    const [mathRendered, setMathRendered] = useState(false);
    useEffect(() => {
        scrolling && scrolling(ref, mathRendered);
    }, [scrolling, mathRendered]);
    const onRendered = useCallback(() => setMathRendered(true), []);

    return (
        <InsertedContainer
            selected={selected}
            disabled={disabled}
            id={id}
            onClick={handleClick}
            tabIndex="0"
            isDragging={isDragging}
            isOver={isOver}
        >
            <InsertedBadge
                disabled={disabled}
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
            >
                <Icon>
                    <RemoveIcon disabled={disabled} />
                </Icon>
            </InsertedBadge>
            <Value onScroll={() => scrolling(ref)} ref={ref}>
                <MathContent onRendered={onRendered}>{value}</MathContent>
            </Value>
        </InsertedContainer>
    );
};

InsertedChip.propTypes = {
    disabled: PropTypes.bool,
    id: PropTypes.string,
    handleClick: PropTypes.func,
    selected: PropTypes.bool,
    scrolling: PropTypes.func,
    value: PropTypes.string,
    onClose: PropTypes.func,
    isDragging: PropTypes.bool,
    isOver: PropTypes.bool,
};

export default InsertedChip;
